import React, { useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Fab
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';

const LocationSelectionStep = ({ formData, setFormData, onBack, onNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const continueButtonRef = useRef(null);

  // Auto-scroll to continue button when location is selected
  useEffect(() => {
    if (formData.hasPhysicalLocation && isMobile && continueButtonRef.current) {
      setTimeout(() => {
        continueButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 300);
    }
  }, [formData.hasPhysicalLocation, isMobile]);

  const options = [
    {
      value: 'yes',
      title: 'Oui',
      subtitle: 'J\'ai un local commercial',
      icon: BusinessIcon
    },
    {
      value: 'no',
      title: 'Non',
      subtitle: 'J\'exerce mon activité à domicile',
      icon: HomeIcon
    }
  ];

  if (isMobile) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            mb: 3, 
            color: 'white',
            fontSize: '1.1rem',
            textAlign: 'center',
            lineHeight: 1.3
          }}
        >
          Exercez-vous dans un local commercial ?
        </Typography>
        
        <Stack spacing={3} sx={{ flex: 1, mb: 4 }}>
          {options.map((option) => {
            const IconComponent = option.icon;
            const isSelected = formData.hasPhysicalLocation === option.value;
            
            return (
              <Card 
                key={option.value}
                sx={{ 
                  cursor: 'pointer',
                  border: isSelected ? '2px solid #f5f5dc' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  transition: 'all 0.2s ease',
                  background: isSelected
                    ? 'linear-gradient(135deg, rgba(245,245,220,0.15) 0%, rgba(232,232,200,0.15) 100%)'
                    : 'rgba(255,255,255,0.05)',
                  '&:active': {
                    transform: 'scale(0.98)'
                  },
                  minHeight: 80
                }}
                onClick={() => setFormData(prev => ({ ...prev, hasPhysicalLocation: option.value }))}
              >
                <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: isSelected 
                        ? 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)'
                        : 'rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                      flexShrink: 0
                    }}
                  >
                    <IconComponent 
                      sx={{ 
                        fontSize: 24, 
                        color: isSelected ? '#212121' : '#f5f5dc'
                      }} 
                    />
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        mb: 0.5
                      }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.85rem',
                        lineHeight: 1.3
                      }}
                    >
                      {option.subtitle}
                    </Typography>
                  </Box>
                  
                  <ArrowForwardIcon 
                    sx={{ 
                      color: isSelected ? '#f5f5dc' : 'rgba(255,255,255,0.4)',
                      fontSize: '1.5rem',
                      ml: 2
                    }} 
                  />
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        <Box display="flex" gap={2} ref={continueButtonRef}>
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                borderColor: '#f5f5dc',
                backgroundColor: 'rgba(245,245,220,0.1)',
                color: '#f5f5dc'
              }
            }}
          >
            Retour
          </Button>
          <Button
            variant="contained"
            disabled={!formData.hasPhysicalLocation}
            onClick={onNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              flex: 2,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.9rem',
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              color: '#212121',
              boxShadow: '0 4px 15px rgba(245, 245, 220, 0.3)',
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            Continuer
          </Button>
        </Box>

        {/* Mobile scroll indicator */}
        {!formData.hasPhysicalLocation && (
          <Fab
            size="small"
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              color: '#212121',
              boxShadow: '0 4px 20px rgba(245, 245, 220, 0.3)',
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)'
                },
                '40%': {
                  transform: 'translateY(-10px)'
                },
                '60%': {
                  transform: 'translateY(-5px)'
                }
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)'
              }
            }}
            onClick={() => {
              window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth'
              });
            }}
          >
            <KeyboardArrowDownIcon />
          </Fab>
        )}
      </Box>
    );
  }

  // Desktop version
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white' }}>
        Exercez-vous dans un local commercial ?
      </Typography>
      
      <Stack spacing={2}>
        {options.map((option) => {
          const IconComponent = option.icon;
          const isSelected = formData.hasPhysicalLocation === option.value;
          
          return (
            <Card 
              key={option.value}
              sx={{ 
                cursor: 'pointer',
                border: isSelected ? '3px solid #f5f5dc' : '2px solid rgba(255,255,255,0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                background: isSelected
                  ? 'linear-gradient(135deg, rgba(245,245,220,0.1) 0%, rgba(232,232,200,0.1) 100%)'
                  : 'rgba(255,255,255,0.05)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(245, 245, 220, 0.2)',
                  border: '3px solid #f5f5dc',
                  background: 'rgba(245,245,220,0.1)'
                }
              }}
              onClick={() => setFormData(prev => ({ ...prev, hasPhysicalLocation: option.value }))}
            >
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconComponent sx={{ color: '#f5f5dc', fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {option.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <ArrowForwardIcon sx={{ color: isSelected ? '#f5f5dc' : 'rgba(255,255,255,0.6)' }} />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#f5f5dc'
            }
          }}
        >
          Retour
        </Button>
        <Button
          variant="contained"
          disabled={!formData.hasPhysicalLocation}
          onClick={onNext}
          endIcon={<ArrowForwardIcon />}
          sx={{ 
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
            color: '#212121',
            boxShadow: '0 8px 25px rgba(245, 245, 220, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(245, 245, 220, 0.4)'
            },
            '&:disabled': {
              background: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.3)'
            }
          }}
        >
          Continuer
        </Button>
      </Box>
    </Box>
  );
};

export default LocationSelectionStep;